// Globals
import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { groupBy } from 'lodash';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { updateDeliverySettings } from '../../feature';

// Modules
import { Header } from '@/modules/Layout';
import { useGetShippingPrices } from '@/modules/ShippingPrices/hooks';
import { getStatesCodeOptions } from '@/modules/ShippingPrices/helpers';
import { selectChannel } from '@/modules/Channels';

//Components
import { Form, useForm } from '@/components';
import { CheckIcon } from '@/icons';
import { Button, Spinner, TSelectOptions } from '@/ui';
import { FreeDeliveryFormSection, UPSFormSection } from '../../components';
import { StyledContainer } from './DeliverySettingsContainerStyles';

// Helpers
import { UpdateDeliverySettingsDTO } from '../../dtos';
import {
  DELIVERY_SETTINGS_FORM_ID,
  getDeliverySettingsInitialValues,
  TDeliverySettingsFormValues,
} from '../../helpers';
import { useGetDeliverySettings } from '../../hooks';

const DeliverySettingsContainer: FC = () => {
  const { t } = useTranslation(['common']);
  const dispatch = useAppDispatch();

  const currentChannel = useAppState(selectChannel);
  const { deliverySettings, isLoading } = useGetDeliverySettings(currentChannel?.id);
  const { shippingPrices } = useGetShippingPrices();
  const [formInstance] = useForm();

  const initialValues = useMemo(() => getDeliverySettingsInitialValues(deliverySettings), [deliverySettings]);

  useEffect(() => {
    if (deliverySettings) {
      formInstance.setFieldsValue(getDeliverySettingsInitialValues(deliverySettings));
    }
  }, [formInstance, deliverySettings]);

  const countriesStatesOptions = useMemo(() => {
    const pricesGroupedByCountry = groupBy(shippingPrices, 'countryId');

    return {
      canadianStates: getStatesCodeOptions(pricesGroupedByCountry.CA || []) as TSelectOptions<string>,
      americanStates: getStatesCodeOptions(pricesGroupedByCountry.US || []) as TSelectOptions<string>,
    };
  }, [shippingPrices]);

  const handleSubmitForm = async (values: TDeliverySettingsFormValues) => {
    if (!currentChannel) return;

    const dto = new UpdateDeliverySettingsDTO({ ...values, channelId: currentChannel.id });

    try {
      await dispatch(updateDeliverySettings(dto)).unwrap();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <Header title={t('menu_delivery_settings')} suptitle={t('menu_website_management')}>
        <Button
          type="primary"
          size="small"
          loading={isLoading}
          form={DELIVERY_SETTINGS_FORM_ID}
          htmlType="submit"
          icon={<CheckIcon />}
        >
          {t('btn_save')}
        </Button>
      </Header>
      <Spinner spinning={isLoading}>
        {!!deliverySettings && (
          <Form
            name={DELIVERY_SETTINGS_FORM_ID}
            onFinish={handleSubmitForm}
            initialValues={initialValues}
            form={formInstance}
          >
            <StyledContainer>
              <FreeDeliveryFormSection formInstance={formInstance} {...countriesStatesOptions} />
              <UPSFormSection formInstance={formInstance} {...countriesStatesOptions} />
            </StyledContainer>
          </Form>
        )}
      </Spinner>
    </>
  );
};

export default DeliverySettingsContainer;
